<template>
  <expandable-textarea v-if="showTextArea" ref="input" :value="localValue" @input="formatInput" :disabled="disabled"
            :class="commonEntriesPropertiesCssClasses" v-tooltip.wrap="tooltipIfDisabled" v-bind="$attrs"></expandable-textarea>

  <security-mask
    v-else-if="securityMaskingEnabled"
    :masked-values="securityMaskedValues"
    :class="commonEntriesPropertiesCssClasses"
    v-tooltip.wrap="tooltipIfDisabled"
    v-bind="$attrs"
  ></security-mask>

  <multi-input v-else-if="formatAsPercentage" role="row" v-tooltip.wrap="tooltipIfDisabled">
    <div class="column" role="cell">
      <input
          ref="input"
          :value="percentageInputValue"
          v-bind="{ ...$attrs, type: inputType }"
          :disabled="disabled"
          :class="commonEntriesPropertiesCssClasses"
      >
    </div>

    <suffix role="cell">
      <p>%</p>
    </suffix>
  </multi-input>

  <input v-else ref="input" :value="localValue" v-bind="{ ...$attrs, type: inputType }" @input="formatInput" :disabled="disabled"
         :class="commonEntriesPropertiesCssClasses" v-tooltip.wrap="tooltipIfDisabled">
</template>

<script lang="ts">
import Vue from "vue";
import localValueEmissionMixin from "../../mixins/localValueEmissionMixin";
import {ObjectModelPropertyCapability} from "~/models/objectModelProperties/objectModelPropertyCapability";
import objectModelEntryPropertiesMixin from "../../mixins/objectModelEntries/objectModelEntryPropertiesMixin";
import {InputFormatType} from "../../../formability/form_builder/models/inputFormatType";
import IMask from "imask";
import {ObjectModelPropertyRootType} from "~/enums/objectModelPropertyType";
import SecurityMask from "./object-model-entry-security-mask.vue";
import {IMaskerOptionsArgs, InputMaskerOptions} from "~/helpers/inputMaskerOptions";

export default Vue.extend({
  inheritAttrs: false,
  mixins: [objectModelEntryPropertiesMixin, localValueEmissionMixin],
  components: {
    SecurityMask,
  },
  data() {
    return {
      mask: null,
      isSecurityMasked: true,
    }
  },
  methods: {
    formatInput($event): void {
      if (this.isMaskable) {
        // When masking is enabled, we don't know the order in which our event handler runs with respect to the mask's,
        // so the mask's value may not be correct here. So instead of emitting the input event now, we emit in the
        // "accept" event handler on the mask itself, at which point the value will always be up to date.
        return;
      }

      let input = $event.target.value.trim();
      if (this.objectModelProperty.can(ObjectModelPropertyCapability.numericFormattable)) {
        if (this.objectModelProperty.decimal_places !== null) {
          const decimalPlaces = this.objectModelProperty.decimal_places;
          // Done this way to prevent rounding through parseFloat(toFixed()) methodology.
          // Also handled upon submission by validator.
          const currentPlaces = input.split('.')[1];
          if (currentPlaces && currentPlaces.length > decimalPlaces) {
            // if input decimal places exceed total allowed places, set ref and input to localValue before emitting.
            input = this.localValue;
            this.$refs.input.value = this.localValue;
          }
        }
      }
      this.$emit('input', input);
    },
    initMasker() {
      if (!this.isMaskable) return;

      const input = this.$refs.input;

      this.mask = IMask(input, InputMaskerOptions(this.maskerOptionArgs));

      this.mask.on("accept", () => {
        let newVal = this.mask.value;

        if (this.formatAsPercentage) {
          newVal = parseFloat(newVal) / 100 || null;
        }

        this.$emit("input", newVal);
      });
    },
    updateMask(): void {
      if (!this.mask) return;

      this.mask.updateValue();
      this.mask.updateControl();
    },
  },
  computed: {
    percentageInputValue(): string {
      if (!this.formatAsPercentage || this.localValue === null) return;
      if (this.mask) return this.mask.value;

      const num = this.localValue * 100;
      return num.toFixed(this.percentageDisplayDecimalPlaces);
    },
    inputType(): string {
      return this.isMaskable ? 'text' : this.$attrs.type;
    },
    percentageDisplayDecimalPlaces(): number {
      return Math.max(this.objectModelProperty.decimal_places - 2, 0)
    },
    showTextArea(): boolean {
      return this.objectModelPropertyType === ObjectModelPropertyRootType.LongText && this.isDetailViewEdit
    },
    isMaskable(): boolean {
      return this.maskerOptionArgs !== null;
    },
    securityMaskedValues(): {unmasked?: string, masked: string} {
      return this.entry[`f_${this.objectModelProperty.client_id}_display`];
    },
    securityMaskingEnabled(): boolean {
      return this.disabled && this.objectModelProperty.security_maskable_enabled && this.securityMaskedValues;
    },
    maskerOptionArgs(): IMaskerOptionsArgs {
      const formatType: InputFormatType = this.formatAsPercentage ? InputFormatType.Percentage : this.objectModelProperty.format_type;

      switch (formatType) {
        case InputFormatType.Phone:
          return {
            maskType: 'phone',
          };
        case InputFormatType.SocialSecurity:
          return {
            maskType: 'ssn',
          };
        case InputFormatType.Zip:
          return {
            maskType: 'zip',
          };
        case InputFormatType.Percentage:
          return {
            maskType: 'percent',
            decimalPlaces: this.percentageDisplayDecimalPlaces,
          };
        default:
          return null;
      }
    },
  },
  mounted() {
    this.initMasker();
  },
})
</script>